// 月曆（純函式）：月份的週格、每天的遊戲、只公布到月份的遊戲、月份切換。
import { epochDay, fromEpochDay, weekday } from '@dayone/core/time';
import type { CatalogItem } from './catalog.ts';

const MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/;

export function parseMonth(key: string): { y: number; m: number } | null {
  const r = MONTH.exec(key);
  return r ? { y: Number(r[1]), m: Number(r[2]) } : null;
}

export const monthKey = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}`;

/** 前後月份：'2026-12' + 1 → '2027-01' */
export function shiftMonth(key: string, delta: number): string {
  const p = parseMonth(key);
  if (!p) return key;
  const i = p.y * 12 + (p.m - 1) + delta;
  return monthKey(Math.floor(i / 12), (i % 12) + 1);
}

/** 某天（epoch day）所在的月份 */
export function monthOfDay(day: number): string {
  const { y, m } = fromEpochDay(day);
  return monthKey(y, m);
}

/** 月曆的週格：從週日開始，涵蓋整個月份（前後補上鄰近月份的日期），每週 7 個 epoch day */
export function monthWeeks(key: string): number[][] {
  const p = parseMonth(key);
  if (!p) return [];
  const first = epochDay(p.y, p.m, 1);
  const last = epochDay(p.m === 12 ? p.y + 1 : p.y, p.m === 12 ? 1 : p.m + 1, 1) - 1;
  const start = first - weekday(first);
  const weeks: number[][] = [];
  for (let d = start; d <= last; d += 7) weeks.push(Array.from({ length: 7 }, (_, i) => d + i));
  return weeks;
}

/** 該月份每天發售的遊戲（確定日期），以及只公布到月份的遊戲 */
export function monthGames(
  items: readonly CatalogItem[],
  key: string,
): { byDay: Map<number, CatalogItem[]>; undated: CatalogItem[]; dated: number } {
  const p = parseMonth(key);
  const byDay = new Map<number, CatalogItem[]>();
  const undated: CatalogItem[] = [];
  let dated = 0;
  if (!p) return { byDay, undated, dated };
  for (const item of items) {
    const d = item.date;
    if (d.p === 'day' && d.y === p.y && d.m === p.m) {
      const day = epochDay(d.y, d.m, d.d);
      const list = byDay.get(day);
      if (list) list.push(item);
      else byDay.set(day, [item]);
      dated++;
    } else if (d.p === 'month' && d.y === p.y && d.m === p.m) {
      undated.push(item);
    }
  }
  // 同一天依期待度排列（封面堆疊顯示前 3 款）
  for (const list of byDay.values()) list.sort((a, b) => (b.row.hy ?? 0) - (a.row.hy ?? 0));
  return { byDay, undated, dated };
}
